import { useEffect, useState } from 'react';
import { Form } from '@base-ui-components/react/form';
import { Field } from '@base-ui-components/react/field';
import { identity } from '../brand';

type Step = 'email' | 'sent';

export function LoginForm({ initialError }: { initialError?: string }) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState(initialError ? 'That link has expired — request a new one.' : '');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function requestLink(addr: string) {
    setLoading(true);
    setErrors({});
    try {
      const res = await fetch('/api/auth/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string; magicLink?: string };
      if (!res.ok || !data.ok) {
        setErrors({ email: data.error || 'could not send the link. try again.' });
        return;
      }
      // Local dev returns the link directly (no inbox) — sign in in one click.
      // In prod no link comes back, so we show the "check your email" step.
      if (data.magicLink) {
        window.location.href = data.magicLink;
        return;
      }
      setStep('sent');
      setCooldown(30);
    } catch {
      setErrors({ email: 'network error. check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }

  // "viable" email → has @ and a real-looking domain (drives the arrow state)
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  return (
    <div className="admincard">
      <img className="admincard__avatar" src={identity.avatar} width={64} height={64} alt="" />
      <h1 className="admincard__title">Admin console</h1>

      {notice && <p className="admincard__notice">{notice}</p>}

      {step === 'email' ? (
        <Form
          className="admincard__form"
          errors={errors}
          onSubmit={(e) => {
            e.preventDefault();
            setNotice('');
            requestLink(email);
          }}
        >
          <Field.Root name="email" className="field">
            <Field.Label className="field__label">Email</Field.Label>
            <Field.Control
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              autoComplete="email"
              autoFocus
              disabled={loading}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({});
              }}
              className="field__input"
            />
            <Field.Error className="field__error" />
          </Field.Root>

          <button type="submit" className={`btn btn--primary${valid ? ' is-valid' : ''}`} disabled={!valid || loading}>
            <span>{loading ? 'Sending…' : 'Send magic link'}</span>
            {!loading && (
              <span className="btn__go" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  {valid ? <path d="M5 12h13M13 6l6 6-6 6" /> : <path d="M9 6l6 6-6 6" />}
                </svg>
              </span>
            )}
          </button>
        </Form>
      ) : (
        <div className="admincard__sent">
          <div className="admincard__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5l4.5 4.5L19 6.5" />
            </svg>
          </div>
          <p className="admincard__sent-email">Check your email</p>
          <p className="admincard__sent-lead">
            If <b>{email}</b> is an admin, a magic link is on its way.
          </p>
          <p className="admincard__sent-hint">Tap it on this device to enter the console.</p>

          <a
            className="btn btn--primary"
            href="https://mail.google.com/mail/u/0/#search/from%3Adjlongbeard+newer_than%3A1h"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Gmail
          </a>

          <button
            type="button"
            className="btn btn--ghost"
            disabled={cooldown > 0 || loading}
            onClick={() => requestLink(email)}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend link'}
          </button>
          <button
            type="button"
            className="btn btn--link"
            onClick={() => {
              setStep('email');
              setErrors({});
            }}
          >
            Use a different email
          </button>
        </div>
      )}
    </div>
  );
}
